import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { ref, onValue, get, set, remove, off, update, push } from 'firebase/database';
import {
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from 'firebase/storage';
import { useAuth } from 'src/views/authentication/AuthContext';
import { database, storage } from 'src/book/src/firebase';
import { NestCamWiredStandTwoTone } from '@mui/icons-material';
import { executeApiRequest } from 'src/book/src/utilities/utilities';
import { apiBaseUrl } from 'src/getConfig';

const MAX_COVER_IMAGES = 8;
const MAX_COVER_FILE_BYTES = 8 * 1024 * 1024;

const BusinessSettingsContext = createContext();

export const BusinessSettingsProvider = ({ children }) => {
  const { bizId, user, isFeatureEnabled } = useAuth();
  const [aptServicesActive, setAptServicesActive] = useState([]);
  const [aptServicesActiveSnapshot, setAptServicesActiveSnapshot] = useState({});
  const [categories, setCategories] = useState([]);

  const [addOns, setAddOns] = useState([]);
  const [coverImages, setCoverImages] = useState([]);
  const coverImagesRef = useRef(coverImages);

  useEffect(() => {
    coverImagesRef.current = coverImages;
  }, [coverImages]);

  const timeAdded = new Date();

  const fetchAptServices = async () => {
    // fetch
    // parse
    // format
    // assign
  };

  const changeAptRanks = async (newApts) => {
    const servicesRankUpdated = aptServicesActiveSnapshot;

    const newCategoriesRank = newApts.map((category, index) => ({
      ...category,
      rank: index + 1,
    }));

    for (const newCategoryRank of newCategoriesRank) {
      servicesRankUpdated[newCategoryRank.id].rank = newCategoryRank.rank;
    }

    const categoriesRef = ref(database, `/biz/configs/${bizId}/bizApts/active`);

    try {
      await update(categoriesRef, servicesRankUpdated);
    } catch (error) {
      console.log(error);
    }
  };

  const getAptDetails = (aptId) => {
    if (!aptId || !aptServicesActiveSnapshot) return null;

    if (aptServicesActiveSnapshot[aptId]) {
      return aptServicesActiveSnapshot[aptId];
    }

    return null;
  };

  // Function to fetch active notifications
  useEffect(() => {
    if (!bizId) {
      setAptServicesActive([]);
      setAptServicesActiveSnapshot({});

      return;
    }

    const servicesRef = ref(database, `biz/configs/${bizId}/bizApts`);

    const onValueChange = onValue(
      servicesRef,
      (snapshot) => {
        if (snapshot.exists()) {
          const snapshotValue = snapshot.val();

          const activeServicesSnapshot = snapshotValue.active ?? null;

          const activeServicesParsed = Object.entries(activeServicesSnapshot ?? {}).map(
            ([key, value]) => ({
              id: key,
              ...value,
            }),
          );

          const sortedServices = activeServicesParsed.sort((a, b) => {
            // Assume a large number for missing ranks
            const rankA = a.rank || Number.MAX_SAFE_INTEGER;
            const rankB = b.rank || Number.MAX_SAFE_INTEGER;

            return rankA - rankB;
          });
          // notificationsArray.sort((b, a) => parseInt(a.time) - parseInt(b.time));

          setAptServicesActive(sortedServices);
          setAptServicesActiveSnapshot(activeServicesSnapshot);
        } else {
          setAptServicesActive([]);
          setAptServicesActiveSnapshot({});
        }
      },
      (error) => {
        console.error('Firebase read failed: ', error);
        setAptServicesActiveSnapshot({});
        setAptServicesActive([]);
      },
    );

    // Return function to clean up the subscription
    return () => {
      off(servicesRef, 'value', onValueChange);
    };
  }, [bizId]);

  const cleanObject = (obj) => {

    const cleanEntries = ([key, value]) => {
      // Remove undefined values entirely
      if (value === undefined) {
        return null; // This will be filtered out
      }
      // For description field specifically, remove empty strings entirely
      if (key === 'description' && value === '') {
        return null; // This will be filtered out
      }
      // For other empty strings, set to null
      if (value === '') {
        return [key, null];
      } else if (Array.isArray(value)) {
        return [
          key,
          value.map((v) => {
            if (typeof v === 'object' && v !== null) {
              return cleanObject(v);
            } else if (v === '') {
              return null; // Convert empty strings in arrays to null
            }
            return v;
          }),
        ];
      } else if (typeof value === 'object' && value !== null) {
        return [key, cleanObject(value)];
      }
      return [key, value];
    };

    return Object.fromEntries(
      Object.entries(obj).map(cleanEntries).filter(entry => entry !== null) // Filter out null entries
    );
  };

  // Function to fetch appointment categories
  useEffect(() => {
    if (!bizId) {
      setCategories([]);
      return;
    }

    const categoriesRef = ref(database, `biz/configs/${bizId}/aptCategories`);

    const onValueChange = onValue(
      categoriesRef,
      (snapshot) => {
        if (snapshot.exists()) {
          const snapshotValue = snapshot.val();

          const aptCategoriesSnapshot = snapshotValue ?? null;

          const categoriesParsed = Object.entries(aptCategoriesSnapshot).map(([key, value]) => ({
            id: key,
            ...value,
          }));
          // notificationsArray.sort((b, a) => parseInt(a.time) - parseInt(b.time));

          setCategories(categoriesParsed);
        } else {
          setCategories([]);
        }
      },
      (error) => {
        console.error('Firebase read failed: ', error);
        setCategories([]);
      },
    );

    return () => {
      off(categoriesRef, 'value', onValueChange);
    };
  }, [bizId]);

  const getCategory = (categoryId) => {
    const category = categories.filter((cat) => cat.id === categoryId);
    if (category.length === 1) {
      return category[0];
    }
    return null;
  };

  const getAddOn = (addOnId) => {
    const addOn = addOns.filter((add) => add.id === addOnId);
    if (addOn.length === 1) {
      return addOn[0];
    }
    return null;
  };

  // Function to fetch Add ons
  useEffect(() => {
    if (!bizId) {
      setAddOns([]);
      return;
    }

    const addOnsRef = ref(database, `biz/configs/${bizId}/addOns`);

    const onValueChange = onValue(
      addOnsRef,
      (snapshot) => {
        if (snapshot.exists()) {
          const snapshotValue = snapshot.val();

          const addOnsSnapshot = snapshotValue ?? null;

          const addOnsParsed = Object.entries(addOnsSnapshot).map(([key, value]) => ({
            id: key,
            strings: value.addOnName,
            ...value,
          }));

          const addOnsParsedFilteres = addOnsParsed.filter((elemnt) => elemnt.isHidden !== true);

          setAddOns(addOnsParsedFilteres);
        } else {
          setAddOns([]);
        }
      },
      (error) => {
        console.error('Firebase read failed: ', error);
        setAddOns([]);
      },
    );

    return () => {
      off(addOnsRef, 'value', onValueChange);
    };
  }, [bizId]);

  const editAptCategories = async (aptId, newCategoryIds) => {
    const aptPath = `biz/configs/${bizId}/bizApts/active/${aptId}/`;
    const aptRef = ref(database, aptPath);

    let aptData = {
      categoryIds: newCategoryIds,
    };

    try {
      await update(aptRef, aptData);
    } catch (e) {
      console.log(e);
    }
  };

  const parseIntSafe = (value) => {
    const parsed = parseInt(value);
    return isNaN(parsed) ? null : parsed;
  };

  const parseDecimalSafe = (value) => {
    if (value === '' || value === null || value === undefined) {
      return null;
    }
    const parsed = parseFloat(value);
    return isNaN(parsed) ? null : parsed;
  };

  const addEditAptService = async (
    durationMins,
    title,
    internalTitle,
    description,
    price,
    downPaymentCost,
    cancelFullRefundDaysBefore,
    addOnIdsOptions,
    categoryIds,
    serviceId = null,
    aptDetials = null,
    color = 'blue',
    showOnlyInDashboard = false,
  ) => {
    if (internalTitle === undefined || internalTitle === '') {
      internalTitle = null;
    }
    const aptPath = `biz/configs/${bizId}/bizApts/active/${serviceId ? serviceId : ''}`;

    const aptRef = ref(database, aptPath);
    const now = new Date();

    const wasHiddenInDashboard = aptDetials?.overrideHiddenInDashboard || false;
    const isTogglingOn = !wasHiddenInDashboard && showOnlyInDashboard;
    const isTogglingOff = wasHiddenInDashboard && !showOnlyInDashboard;
    const isEditingWhileOn = wasHiddenInDashboard && showOnlyInDashboard;

    let aptData = {
      durationMins,
      lastUpdatedTimestamp: timeAdded.getTime(),
      timestampAdded: aptDetials?.timestampAdded ?? now.getTime(),
      title,
      addOnIdsOptions,
      internalTitle,
      cancelFullRefundDaysBefore: parseIntSafe(cancelFullRefundDaysBefore),
      description,
      downPaymentCost: parseDecimalSafe(downPaymentCost),
      price: parseDecimalSafe(price),
      categoryIds,
      color,
    };

    aptData = cleanObject(aptData);

    if (serviceId) {
      try {
        // Handle toggle OFF: Remove both fields together
        if (isTogglingOff) {
          await update(aptRef, { ...aptData, overrideHiddenInDashboard: null, isHidden: null });
        }
        // Handle toggle ON: Add overrideHiddenInDashboard, wait 0.5s, then add isHidden
        else if (isTogglingOn) {
          await update(aptRef, { ...aptData, overrideHiddenInDashboard: true });
          await new Promise(resolve => setTimeout(resolve, 500));
          await update(aptRef, { isHidden: true });
        }
        // Handle editing while toggle is ON: Remove isHidden, wait 0.5s, update, wait 0.5s, restore isHidden
        else if (isEditingWhileOn) {
          await update(aptRef, { isHidden: null });
          await new Promise(resolve => setTimeout(resolve, 500));
          await update(aptRef, { ...aptData, overrideHiddenInDashboard: true });
          await new Promise(resolve => setTimeout(resolve, 500));
          await update(aptRef, { isHidden: true });
        }
        // Normal update when toggle is OFF and staying OFF
        else {
          await update(aptRef, aptData);
        }
      } catch (e) {
        console.log(e);
      }
    } else {
      try {
        aptData.timestampAdded = timeAdded.getTime();

        if (isFeatureEnabled('restricted_scheduling')) {
          aptData.restrictedSchedulingFilter = true;
        }

        // For new service, if toggle is ON, add overrideHiddenInDashboard, wait 0.5s, then add isHidden
        if (showOnlyInDashboard) {
          aptData.overrideHiddenInDashboard = true;
          const newService = await push(aptRef, aptData);
          await new Promise(resolve => setTimeout(resolve, 500));
          await update(ref(database, `${aptPath}${newService.key}`), { isHidden: true });
          return newService.key;
        } else {
          const newService = await push(aptRef, aptData);
          return newService.key;
        }
      } catch (e) {
        console.log(e);
      }
    }
  };

  const editAptStaffMembers = async (newStaffMembers, aptId, currentStaffMembers) => {
    // iterate over the new staff Members, and Add the ones which dont appear in old members.
    for (const staff of newStaffMembers) {
      if (currentStaffMembers.some((currentMember) => currentMember.id === staff.id)) {
        // new staff already exist, nothing to do:
        continue;
      } else {
        const response = await executeApiRequest(
          `${apiBaseUrl}/staffAptOptionChange`,
          'POST',
          {
            bizId: bizId,
            staffId: staff.id,
            aptId: aptId,
            operationType: 'Add',
          },
          user.accessToken,
        );
      }
    }

    // iterate over the old members, and remove the ones which dont appear in new staff members.
    for (const staff of currentStaffMembers) {
      if (newStaffMembers.some((currentMember) => currentMember.id === staff.id)) {
        continue;
      } else {
        const response = await executeApiRequest(
          `${apiBaseUrl}/staffAptOptionChange`,
          'POST',
          {
            bizId: bizId,
            staffId: staff.id,
            aptId: aptId,
            operationType: 'Remove',
          },
          user.accessToken,
        );
      }
    }
  };

  const getAptCategories = (categoriesIds) => {};

  const addEditCategory = async (title, description, rank, color, categoryId = null) => {
    const aptPath = `biz/configs/${bizId}/aptCategories/${categoryId ? categoryId : ''}`;

    const aptRef = ref(database, aptPath);
    const now = new Date();

    let categoryData = {
      title,
      description,
      rank,
      color,
    };

    categoryData = cleanObject(categoryData);

    if (categoryId) {
      try {
        await update(aptRef, categoryData);
      } catch (e) {
        console.log(e);
      }
    } else {
      try {
        // aptData.timestampAdded = timeAdded.getTime();
        await push(aptRef, categoryData);
      } catch (e) {
        console.log(e);
      }
    }
  };

  const removeCategory = async (categoryId) => {
    if (!categoryId) {
      console.error('No category ID provided');
      return;
    }

    const aptPath = `biz/configs/${bizId}/aptCategories/${categoryId}`;
    const aptRef = ref(database, aptPath);

    try {
      await remove(aptRef);
      // console.log('Category removed successfully');
    } catch (e) {
      console.error('Error removing category:', e);
    }
  };

  const changeStaffRank = async (staff) => {
    const newStaffRank = staff.map((category, index) => ({
      ...category,
      rank: index + 1,
    }));

    for (const staffMember of newStaffRank) {
      const aptPath = `biz/configs/${bizId}/staff/${staffMember.id}`;
      const aptRef = ref(database, aptPath);

      try {
        await update(aptRef, { rank: staffMember.rank });
        // console.log('Category removed successfully');
      } catch (e) {
        console.error('Error removing category:', e);
      }
    }
  };

  const removeStaff = async (staffId, operation) => {
    if (!staffId) {
      console.error('No staff id provided');
      return;
    }

    const aptPath = `biz/configs/${bizId}/staff/${staffId}`;
    const aptRef = ref(database, aptPath);

    try {
      await update(aptRef, { isHidden: operation === 'delete' ? true : false });
      console.log('Category removed successfully');
    } catch (e) {
      console.error('Error removing category:', e);
    }
  };

  const addEditAddOn = async (
    title,
    downPaymentCost,
    durationMins,
    additionalCost = null,
    addOnId = null,
    isHidden = null,
  ) => {
    const aptPath = `biz/configs/${bizId}/addOns/${addOnId ? addOnId : ''}`;

    const aptRef = ref(database, aptPath);
    const now = new Date();

    let categoryData = {
      addOnName: title,
      downPaymentCost: parseInt(downPaymentCost),
      durationMins,
      isHidden,
      additionalCost: parseInt(additionalCost),
    };

    categoryData = cleanObject(categoryData);

    if (addOnId) {
      try {
        await update(aptRef, categoryData);
      } catch (e) {
        console.log(e);
      }
    } else {
      try {
        // aptData.timestampAdded = timeAdded.getTime();
        await push(aptRef, categoryData);
      } catch (e) {
        console.log(e);
      }
    }
  };

  const archiveAptService = async (serviceId) => {
    const activePath = `biz/configs/${bizId}/bizApts/active/${serviceId}`;
    const archivePath = `biz/configs/${bizId}/bizApts/archive/${serviceId}`;
    const aptRef = ref(database, activePath);
    const archiveRef = ref(database, archivePath);

    try {
      const snapshot = await get(aptRef);
      if (snapshot.exists()) {
        let aptData = snapshot.val();
        aptData.archiveTimestamp = new Date().getTime();

        await set(archiveRef, aptData);

        await remove(aptRef);
      } else {
        console.log('No data available to archive.');
      }
    } catch (error) {
      console.error('Failed to archive the appointment:', error);
    }
  };

  useEffect(() => {
    if (!bizId) {
      setCoverImages([]);
      return;
    }

    const coversRef = ref(database, `biz/userInfo/${bizId}/images/covers`);

    const onValueChange = onValue(
      coversRef,
      (snapshot) => {
        if (!snapshot.exists()) {
          setCoverImages([]);
          return;
        }

        const value = snapshot.val();
        if (Array.isArray(value)) {
          setCoverImages(value.filter(Boolean));
        } else if (value && typeof value === 'object') {
          setCoverImages(Object.values(value).filter(Boolean));
        } else {
          setCoverImages([]);
        }
      },
      (error) => {
        console.error('Failed to load cover images:', error);
        setCoverImages([]);
      },
    );

    return () => {
      off(coversRef, 'value', onValueChange);
    };
  }, [bizId]);

  const updateCoverImages = async (covers) => {
    const coversRef = ref(database, `biz/userInfo/${bizId}/images/covers`);
    await set(coversRef, covers);
  };

  const uploadCoverImages = async (files) => {
    if (!files?.length || !bizId) {
      throw new Error('File and business ID are required for uploading.');
    }

    const remaining = MAX_COVER_IMAGES - coverImagesRef.current.length;
    if (remaining <= 0) {
      throw new Error('MAX_IMAGES_REACHED');
    }

    const filesToUpload = Array.from(files).slice(0, remaining);
    if (filesToUpload.some((file) => file.size > MAX_COVER_FILE_BYTES)) {
      throw new Error('FILE_TOO_LARGE');
    }

    const urls = [];
    for (let i = 0; i < filesToUpload.length; i += 1) {
      const file = filesToUpload[i];
      const safeName = file.name.replace(/[^\w.\-]+/g, '_');
      const filePath = `biz/${bizId}/covers/${Date.now()}_${i}_${safeName}`;
      const fileRef = storageRef(storage, filePath);
      const uploadResult = await uploadBytes(fileRef, file);
      urls.push(await getDownloadURL(uploadResult.ref));
    }

    const nextCovers = [...coverImagesRef.current, ...urls];
    coverImagesRef.current = nextCovers;
    setCoverImages(nextCovers);
    await updateCoverImages(nextCovers);
    return urls;
  };

  const uploadCoverImage = async (file) => {
    const urls = await uploadCoverImages([file]);
    return urls[0];
  };

  const deleteCoverImage = async (index) => {
    const url = coverImages[index];
    if (!url) return;

    try {
      const fileRef = storageRef(storage, url);
      await deleteObject(fileRef);
    } catch (error) {
      console.error('Failed to delete cover image from storage:', error);
    }

    const newCovers = coverImages.filter((_, i) => i !== index);
    await updateCoverImages(newCovers);
  };

  const reorderCoverImages = async (newCovers) => {
    setCoverImages(newCovers);
    await updateCoverImages(newCovers);
  };

  const editStaff = async (staffId, title, description, color) => {
    const staffPath = `biz/configs/${bizId}/staff/${staffId}`;

    const aptRef = ref(database, staffPath);
    const now = new Date();

    let staffData = {
      title,
      description,
      color,
    };

    staffData = cleanObject(staffData);

    if (staffId) {
      try {
        await update(aptRef, staffData);
      } catch (e) {
        console.log(e);
      }
    }
  };

  return (
    <BusinessSettingsContext.Provider
      value={{
        addEditAptService,
        aptServicesActive,
        categories,
        addOns,
        getAptCategories,
        changeCategoriesRanks: changeAptRanks,
        getAptDetails,
        editAptStaffMembers,
        addEditCategory,
        addEditAddOn,
        getCategory,
        getAddOn,
        editAptCategories,
        archiveAptService,
        editStaff,
        removeCategory,
        removeStaff,
        changeStaffRank,
        coverImages,
        uploadCoverImage,
        uploadCoverImages,
        deleteCoverImage,
        reorderCoverImages,
        maxCoverImages: MAX_COVER_IMAGES,
      }}
    >
      {children}
    </BusinessSettingsContext.Provider>
  );
};

export const useBusinessSettings = () => useContext(BusinessSettingsContext);
